// import type { VariantProps } from "class-variance-authority";
// import { cva } from "class-variance-authority";
import { VariantProps, cva } from "class-variance-authority";
import {
  CheckIcon,
  ChevronDown,
  PlusIcon,
  Trash2,
  XCircle,
  XIcon,
} from "lucide-react";
import * as React from "react";

import Loading from "../state-pages/Loading";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { ScrollArea } from "../ui/scroll-area";

import { IbtnVariant, LabelValue } from "@/interfaces/interfaces";
import { cn } from "@/lib/utils";
import Icons from "@/utils/Icons";

/**
 * Variants for the multi-select component to handle different styles.
 * Uses class-variance-authority (cva) to define different styles based on "variant" prop.
 */
const multiSelectVariants = cva(
  "m-1 transition ease-in-out delay-150   duration-300",
  {
    variants: {
      variant: {
        default:
          "border-foreground/10 text-foreground bg-card hover:bg-card/80",
        secondary:
          "border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        inverted: "inverted",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

/**
 * Props for MultiSelect component
 */

export interface MultiSelectProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof multiSelectVariants> {
  /**
   * An array of option objects to be displayed in the multi-select component.
   * Each option object has a label, value, and an optional icon.
   */
  options: {
    /** The text to display for the option. */
    label: string;
    /** The unique value associated with the option. */
    value: string;
    /** Optional icon component to display alongside the option. */
    icon?: React.ComponentType<{ className?: string }>;

    /**Optional icon component to display alongside the option. */
    id?: string;
  }[];

  /**
   * Callback function triggered when the selected values change.
   * Receives an array of the new selected values.
   */
  // onValueChange: (value: string[]) => void;
  onValueChange: (value: LabelValue[]) => void;

  /** The default selected values when the component mounts. */
  defaultValue?: string[];
  Values?: LabelValue[];

  /**
   * Placeholder text to be displayed when no values are selected.
   * Optional, defaults to "Select options".
   */
  placeholder?: string;

  /**
   * Animation duration in seconds for the visual effects (e.g., bouncing badges).
   * Optional, defaults to 0 (no animation).
   */
  animation?: number;

  /**
   * Maximum number of items to display. Extra selected items will be summarized.
   * Optional, defaults to 3.
   */
  maxCount?: number;

  /**
   * Maximum number of items can be selected.
   * Optional
   */
  maxSelection?: number;

  /**
   * The modality of the popover. When set to true, interaction with outside elements
   * will be disabled and only popover content will be visible to screen readers.
   * Optional, defaults to false.
   */
  modalPopover?: boolean;

  /**
   * If true, renders the multi-select component as a child of another component.
   * Optional, defaults to false.
   */
  asChild?: boolean;

  /**
   * Additional class names to apply custom styles to the multi-select component.
   * Optional, can be used to add custom styles.
   */
  className?: string;
  isLoading?: boolean;
  /**
   * Additional props to pass to the multi-select component.
   * Optional, can be used to add custom props.
   */
  addButton?:
    | {
        label?: string;
        variant?: IbtnVariant;
        className?: string;
        handleClick?: () => void;
        icon?: string;
      }
    | false;

  /**
   * Additional props to pass to the multi-select component.
   * Optional, can be used to add custom props.
   */
  deleteAction?: (id: string) => void;
  side?: "top" | "right" | "bottom" | "left";
  node?: {
    position: "right";
    node: React.ComponentType<{ id: string }>;
  };
}

export const SelectMultiple = React.forwardRef<
  HTMLButtonElement,
  MultiSelectProps
>(
  (
    {
      options,
      onValueChange,
      variant,
      defaultValue = [],
      placeholder = "Select options",
      animation = 0,
      maxCount = Number.MAX_SAFE_INTEGER,
      modalPopover = false,
      className,
      isLoading,
      Values,
      maxSelection,
      addButton,
      deleteAction,
      node,
      ...props
    },
    ref,
  ) => {
    const [selectedValues, setSelectedValues] = React.useState<LabelValue[]>(
      [],
    );

    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);

    React.useEffect(() => {
      if (
        Values !== undefined &&
        Values.length > 0 &&
        typeof Values[0] === "object"
      ) {
        // Check if Values is an array of objects with label and value properties
        if (Values.every((item) => "label" in item && "value" in item)) {
          //Check if values exist in options
          const matchedOptions = options.filter((option) =>
            Values.some((value) => value.value === option.value),
          );

          setSelectedValues(matchedOptions || []);
        }
      } else if (Array.isArray(defaultValue)) {
        const matchedOptions = options.filter((option) =>
          defaultValue.includes(option.value),
        );
        setSelectedValues(matchedOptions || []);
      }
    }, [Values, defaultValue, options]);

    const handleInputKeyDown = (
      event: React.KeyboardEvent<HTMLInputElement>,
    ): void => {
      if (event.key === "Enter") {
        setIsPopoverOpen(true);
      } else if (event.key === "Backspace" && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues];

        newSelectedValues.pop();
        setSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
      }
    };

    const toggleOption = (single: LabelValue): void => {
      const newSelectedValues = selectedValues.some(
        (v) => v.value === single.value,
      )
        ? selectedValues.filter((v) => v.value !== single.value)
        : [...selectedValues, single];
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    const handleClear = (): void => {
      setSelectedValues([]);
      onValueChange([]);
    };

    const handleTogglePopover = (): void => {
      setIsPopoverOpen((prev) => !prev);
    };

    const clearExtraOptions = (): void => {
      const newSelectedValues = selectedValues.slice(0, maxCount);
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    // const toggleAll = () => {
    //   if (selectedValues.length === options.length) {
    //     handleClear();
    //   } else {
    //     const allValues = options.map((option) => option.value);
    //     setSelectedValues(allValues);
    //     onValueChange(allValues);
    //   }
    // };
    //
    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>): void => {
      if (e.key === "Tab") {
        setIsPopoverOpen(true);
      } else if (e.key === "Escape") {
        setIsPopoverOpen(false);
      }
    };

    return (
      <Popover
        open={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        modal={modalPopover}
      >
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            {...props}
            onClick={handleTogglePopover}
            className={cn(
              "flex w-full p-1 rounded-md border min-h-10 h-auto items-center justify-between bg-inherit hover:bg-inherit active:scale-100 ",
              className,
            )}
            onKeyUp={handleKeyDown}
          >
            {selectedValues.length > 0 ? (
              <div className='flex items-center justify-between w-full'>
                <div className='flex flex-wrap items-center'>
                  {selectedValues.slice(0, maxCount).map((single) => {
                    const option = options.find(
                      (o) => o.value === single.value,
                    );
                    const IconComponent = option?.icon;

                    return (
                      <>
                        <Badge
                          key={single.value}
                          className={cn(
                            "font-normal whitespace-normal",
                            multiSelectVariants({ variant }),
                          )}
                          style={{ animationDuration: `${animation}s` }}
                        >
                          {IconComponent && (
                            <IconComponent className='w-4 h-4 mr-2' />
                          )}
                          {option?.label}
                          <XCircle
                            className='w-4 h-4 ml-2 cursor-pointer'
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleOption(single);
                            }}
                          />
                        </Badge>
                      </>
                    );
                  })}
                  {selectedValues.length > maxCount && (
                    <Badge
                      className={cn(
                        "bg-transparent text-foreground border-foreground/1 hover:bg-transparent",
                        multiSelectVariants({ variant }),
                      )}
                      style={{ animationDuration: `${animation}s` }}
                    >
                      {`+ ${selectedValues.length - maxCount} more`}
                      <XCircle
                        className='w-4 h-4 ml-2 cursor-pointer'
                        onClick={(event) => {
                          event.stopPropagation();
                          clearExtraOptions();
                        }}
                      />
                    </Badge>
                  )}
                </div>
                <div className='flex items-center justify-between'>
                  <XIcon
                    className='h-4 mx-2 cursor-pointer text-muted-foreground'
                    onClick={(event) => {
                      event.stopPropagation();
                      handleClear();
                    }}
                  />
                  <ChevronDown
                    className={cn(
                      "h-4 cursor-pointer text-muted-foreground",
                      selectedValues.length > 0 && "hidden",
                    )}
                  />
                </div>
              </div>
            ) : (
              <div className='flex items-center justify-between w-full'>
                <span className='text-sm text-black font-normal text-left mx-2 w-[75%] truncate opacity-30'>
                  {placeholder}
                </span>
                <ChevronDown className='w-4 h-4 mx-2 cursor-pointer text-muted-foreground ' />
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className='p-2 popover-content-width-full -mt-2 border border-input  rounded-[0.5rem] shadow-select'
          align='start'
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
          portal={false}
          side={props.side}
        >
          <Command
            filter={(value, search) => {
              if (value.includes(search.toLowerCase())) {
                return 1;
              }

              return 0;
            }}
          >
            <CommandInput
              placeholder='Search here'
              onKeyDown={handleInputKeyDown}
            />

            <CommandList className='select-scrollbar-hide'>
              <CommandEmpty firstRender={true}>No results found.</CommandEmpty>
              <CommandGroup className=' px-0 !mx-0 '>
                {/* <CommandItem
                  key='all'
                  onSelect={toggleAll}
                  className='font-medium cursor-pointer'
                >
                  <span>
                    {options.length === selectedValues.length
                      ? "Unselect"
                      : "Select"}
                    All
                  </span>
                </CommandItem> */}
                {isLoading ? (
                  <Loading />
                ) : (
                  <ScrollArea className='overflow-y-scroll select-scrollbar-hide'>
                    {options.map((option, index) => {
                      const isSelected = selectedValues.some(
                        (selected) => selected.value === option.value,
                      );
                      //  this is for css injection
                      const escapedValue = CSS.escape(option?.label);

                      return (
                        <CommandItem
                          key={option.value}
                          value={escapedValue + index}
                          onSelect={() => toggleOption(option)}
                          className='p-3 cursor-pointer content-stretch group'
                          disabled={
                            maxSelection
                              ? selectedValues.length >= maxSelection
                              : false
                          }
                        >
                          <div className='flex items-center justify-between w-full'>
                            <div className='flex items-center flex-1'>
                              <div
                                className={cn(
                                  "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-gray-600",
                                  isSelected
                                    ? "bg-primary text-primary-foreground"
                                    : "opacity-50 [&_svg]:invisible",
                                )}
                              >
                                <CheckIcon className='w-4 h-4' />
                              </div>
                              {option.icon && (
                                <option.icon className='w-4 h-4 mr-2 text-muted-foreground' />
                              )}
                              <span>{option.label}</span>
                            </div>
                            {node && node.position === "right" && (
                              <node.node id={option?.value} />
                            )}
                          </div>
                          {deleteAction && (
                            <div
                              className='absolute invisible p-1 cursor-pointer right-3 top-2 group-hover:visible'
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteAction(option?.id ?? option.value);
                              }}
                            >
                              <Trash2 name='trash' color='red' size={18} />
                            </div>
                          )}
                        </CommandItem>
                      );
                    })}
                  </ScrollArea>
                )}
              </CommandGroup>
            </CommandList>
            {addButton && (
              <Button
                type='button'
                className={cn(
                  "flex justify-center items-center align-center w-full",
                  addButton.className,
                )}
                variant={addButton?.variant || "secondary"}
                onClick={addButton.handleClick}
              >
                {addButton.icon ? (
                  <Icons name={addButton.icon} size={18} color='#fff' />
                ) : (
                  <PlusIcon size={18} strokeWidth={1.8} className='mx-1' />
                )}
                <p className={addButton.icon && "px-2"}>{addButton?.label}</p>
              </Button>
            )}
          </Command>
        </PopoverContent>
      </Popover>
    );
  },
);

SelectMultiple.displayName = "SelectMultiple";
